/**
 * Public legal pages (no auth) - EULA + Privacy Policy.
 *
 * These exist primarily to satisfy third-party platform requirements
 * (e.g. Intuit's App Assessment requires live EULA / privacy URLs)
 * and to give proposal recipients transparency about how the portal
 * handles their data.
 */
import { Link } from "react-router-dom";

function LegalLayout({ title, updated, children }) {
  return (
    <div className="min-h-screen bg-[#FAF9F7] text-slate-800">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-1">
          Medicys Ltd · Proposal Portal
        </p>
        <h1 className="font-serif text-4xl font-light text-slate-900 leading-tight mb-1">
          {title}
        </h1>
        <p className="text-xs text-stone-400 mb-8">Last updated: {updated}</p>
        <div className="space-y-6 text-sm leading-relaxed text-stone-700 [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:text-base [&_h2]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
        <div className="mt-12 pt-6 border-t border-stone-200 text-xs text-stone-400 flex gap-4">
          <Link to="/legal/eula" className="hover:text-[#1B4F8A]">End-User Licence Agreement</Link>
          <Link to="/legal/privacy" className="hover:text-[#1B4F8A]">Privacy Policy</Link>
          <a href="https://medicysltd.co.uk" className="hover:text-[#1B4F8A]">medicysltd.co.uk</a>
        </div>
      </div>
    </div>
  );
}

export function LegalEula() {
  return (
    <LegalLayout title="End-User Licence Agreement" updated="16 July 2026">
      <section data-testid="legal-eula">
        <h2>1. About this application</h2>
        <p>
          The Medicys Proposal Portal (proposals.medicysltd.co.uk, the
          "Service") is a private, internal business application operated by
          Medicys Ltd ("Medicys", "we"). It is used by Medicys staff to
          prepare and manage research proposals, and by invited client
          contacts to view proposals shared with them.
        </p>
        <h2>2. Licence</h2>
        <p>
          Medicys grants authorised users a personal, non-exclusive,
          non-transferable, revocable licence to access the Service solely
          for its intended business purpose. Access for client contacts is
          limited to the specific proposal links shared with them.
        </p>
        <h2>3. Acceptable use</h2>
        <ul>
          <li>Do not share access credentials or gated proposal links with unauthorised parties.</li>
          <li>Do not attempt to access data belonging to other clients or interfere with the operation or security of the Service.</li>
          <li>Proposal content, pricing and documents are confidential business information of Medicys and the intended recipient.</li>
        </ul>
        <h2>4. Intellectual property</h2>
        <p>
          All content within the Service - including proposal documents,
          pricing structures, methodologies and branding - remains the
          property of Medicys Ltd or its licensors.
        </p>
        <h2>5. Third-party services</h2>
        <p>
          The Service integrates with third-party platforms (including
          Microsoft 365, Cognito Forms and Intuit QuickBooks Online) strictly
          to support Medicys' internal business processes. QuickBooks data is
          accessed on a read-only basis for internal financial reporting and
          is never modified by the Service.
        </p>
        <h2>6. Disclaimer and liability</h2>
        <p>
          The Service is provided "as is" for business information purposes.
          Figures shown in proposals are governed by the formal quotation
          documents issued by Medicys. To the maximum extent permitted by
          law, Medicys accepts no liability for indirect or consequential
          loss arising from use of the Service.
        </p>
        <h2>7. Termination</h2>
        <p>
          Medicys may suspend or withdraw access at any time, including the
          expiry of shared proposal links.
        </p>
        <h2>8. Contact</h2>
        <p>
          Questions about this agreement: contact Medicys Ltd via{" "}
          <a href="https://medicysltd.co.uk/contact-us/" className="text-[#1B4F8A] underline">
            medicysltd.co.uk/contact-us
          </a>.
        </p>
      </section>
    </LegalLayout>
  );
}

export function LegalPrivacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="16 July 2026">
      <section data-testid="legal-privacy">
        <h2>1. Who we are</h2>
        <p>
          This policy covers the Medicys Proposal Portal
          (proposals.medicysltd.co.uk), operated by Medicys Ltd, a healthcare
          fieldwork agency registered in the United Kingdom.
        </p>
        <h2>2. What we process</h2>
        <ul>
          <li><strong>Staff accounts</strong> - name, work email and authentication data (including two-factor authentication) for Medicys employees.</li>
          <li><strong>Client contact details</strong> - work email addresses of proposal recipients, used to control access to shared proposals.</li>
          <li><strong>Engagement analytics</strong> - when a recipient views a shared proposal we record activity such as pages viewed, documents downloaded, time and approximate technical context (IP-derived), to help our team follow up appropriately.</li>
          <li><strong>Business records</strong> - proposal, project and accounting information belonging to Medicys, including read-only financial data retrieved from our own QuickBooks Online account for internal reporting.</li>
        </ul>
        <h2>3. Lawful basis</h2>
        <p>
          We process this information under UK GDPR on the basis of
          legitimate interests: operating our proposal workflow, protecting
          confidential pricing, and managing our own financial reporting.
        </p>
        <h2>4. What we do not do</h2>
        <ul>
          <li>We do not sell personal data.</li>
          <li>We do not use recipient data for advertising.</li>
          <li>We do not write to, modify or delete anything in QuickBooks - access is read-only.</li>
          <li>Accounting data retrieved from QuickBooks is not shared with AI services.</li>
        </ul>
        <h2>5. Third-party processors</h2>
        <p>
          The Service relies on established providers to operate: cloud
          hosting, Microsoft 365 (documents and lists), Cognito Forms
          (proposal data), Intuit QuickBooks Online (read-only accounting
          data), and Resend (transactional email). Limited proposal text may
          be processed by Anthropic's Claude to assist staff in drafting
          correspondence; QuickBooks data is excluded from this.
        </p>
        <h2>6. Security and retention</h2>
        <p>
          Access requires authentication (with two-factor authentication for
          staff); shared proposal links are gated to named recipient emails.
          Data is retained only as long as needed for business and legal
          purposes, and access tokens for third-party services are stored
          server-side and encrypted in transit.
        </p>
        <h2>7. Your rights</h2>
        <p>
          Under UK GDPR you may request access to, correction of, or deletion
          of your personal data. Contact us via{" "}
          <a href="https://medicysltd.co.uk/contact-us/" className="text-[#1B4F8A] underline">
            medicysltd.co.uk/contact-us
          </a>{" "}
          and we will respond within one month.
        </p>
      </section>
    </LegalLayout>
  );
}
